import HeaderTitle from '@app/components/LoggedIn/HeaderTitle';
import Box from '@app/components/common/Box';
import Button from '@app/components/common/Button';
import Checkbox from '@app/components/common/Checkbox';
import ColorPicker from '@app/components/common/ColorPicker';
import Icon from '@app/components/common/Icon';
import Switch from '@app/components/common/Switch';
import Table from '@app/components/common/Table';
import InputCell from '@app/components/common/Table/InputCell';
import { ETableModes } from '@app/components/common/Table/types';
import useScrollToNewRow from '@app/hooks/useScrollToNewRow';
import { store } from '@app/state/store';
import { useTheme } from '@emotion/react';
import {
  actionCreatorsSettings,
  actionCreatorsSettingsChangeData,
  DEFAULT_ORDER_SEAT_TAG_COLOR,
  DEFAULT_ORDER_SEAT_TAG_TEXT_COLOR,
  generateTabletGeneratedId,
  getOrderSeatTagId,
  ICustomTender,
  IPaymentsSettings,
  PAYMENTS_CUSTOM_TENDER_INITIAL_STATE,
  settingsSelectors,
} from '@westondev/tableturn-core';
import { m, useMotionValue, useTransform } from 'framer-motion';
import cloneDeep from 'lodash/cloneDeep';
import max from 'lodash/max';
import orderBy from 'lodash/orderBy';
import { useEffect, useMemo } from 'react';
import { WithTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  columnHelper,
  getMemoizedItemData,
  ICustomTendersTable,
  ROW_ID_PREFIX,
  SECTION_ID,
} from './types';

const { customTendersSelector, areSettingsMasterModeSelector } =
  settingsSelectors;

const CustomTenders = ({ t }: WithTranslation) => {
  // Redux
  const dispatch = useDispatch();
  const { updateGenericSettings: setValue } = bindActionCreators(
    actionCreatorsSettings,
    dispatch,
  );
  const clearSettingsError = bindActionCreators(
    actionCreatorsSettingsChangeData.clearSettingsError,
    dispatch,
  );

  const theme = useTheme();

  const y = useMotionValue(0);

  const backgroundColor = useTransform(
    y,
    [0, 10],
    [theme.colors.lightestExtraGrey, theme.colors.white],
  );

  const boxShadow = useTransform(
    y,
    [0, 10],
    [
      '0px 0px 5px 0px rgba(3, 8, 25, 0)',
      '0px 5px 5px 0px rgba(3, 8, 25, 0.10)',
    ],
  );

  useEffect(() => {
    const element = document.getElementById('layout-content');
    if (!element) return;

    element.addEventListener('scroll', handleScroll);
    return () => element.removeEventListener('scroll', handleScroll);

    function handleScroll(e: Event) {
      const scroll = e.target as HTMLDivElement;
      y.set(scroll.scrollTop);
    }
  }, [y]);

  const actualCustomTenders = useSelector(customTendersSelector);
  const isMasterMode = useSelector(areSettingsMasterModeSelector);
  const { data: bigData } = useSelector(getMemoizedItemData);

  // Local state
  const data = bigData?.customTenders;

  const updateCustomTender = (
    newValue: any,
    id: number | string,
    field: keyof ICustomTender,
  ) => {
    const customTenders = (
      store.getState().settings.changeData.data as IPaymentsSettings
    )?.customTenders;
    setValue<IPaymentsSettings>(
      {
        customTenders: {
          ...customTenders,
          [id]: {
            ...customTenders[id],
            [field]: newValue,
          },
        },
      },
      SECTION_ID,
    );
  };

  const handleDelete = (id: number | string) => {
    const customTenders = (
      store.getState().settings.changeData.data as IPaymentsSettings
    )?.customTenders;

    const customTendersCloned = cloneDeep(customTenders);
    delete customTendersCloned[id];

    setValue<IPaymentsSettings>(
      {
        customTenders: customTendersCloned,
      },
      SECTION_ID,
    );
  };

  const handleNewRowScroll = useScrollToNewRow();

  const onNewCustomTender = () => {
    const nextId = generateTabletGeneratedId();

    const nextSortPriority =
      (max(
        Object.values(data || {}).map(
          customTender => customTender.sortPriority,
        ) || [],
      ) || 0) + 1;

    const nextIndex = Object.keys(data || {}).length;

    setValue<IPaymentsSettings>(
      {
        customTenders: {
          ...data,
          [nextId]: {
            ...PAYMENTS_CUSTOM_TENDER_INITIAL_STATE,
            id: nextId,
            customTenderId: nextId,
            sortPriority: nextSortPriority,
          },
        },
      },
      SECTION_ID,
    );

    handleNewRowScroll(`${ROW_ID_PREFIX}${nextIndex}`);
  };

  const COLUMN_VALUES = useMemo(
    () => [
      columnHelper.accessor('status', {
        header: t('app.modals.userDetailsDiffModal.basic.active'),
        cell: info => {
          return (
            <Switch
              key={`switch-${info.row.original.id}`}
              checked={info.getValue()}
              onChange={active => {
                updateCustomTender(active, info.row.original.id, 'active');
              }}
            />
          );
        },
        size: 70,
      }),
      columnHelper.accessor('name', {
        header: t('commonTexts.name'),
        cell: info => (
          <InputCell
            disabled={!info.row.original.canDelete && !isMasterMode}
            required
            placeholder="Name"
            value={info.getValue()}
            onChange={e => {
              updateCustomTender(
                e.currentTarget.value,
                info.row.original.id,
                'name',
              );
            }}
            errorId={`${getOrderSeatTagId(`${info.row.original.id}`, 'Name')}`}
            onFocusError={(errorId, hasError) =>
              hasError && clearSettingsError(errorId)
            }
          />
        ),
        minSize: 140,
      }),
      columnHelper.accessor('allowsTips', {
        header: t('settingsModule.paymentsSettings.customTenders.allowsTips'),
        cell: info => {
          return (
            <Checkbox
              key={`allows-tips-checkbox-${info.row.original.id}-${info.row.original.allowsTips}`}
              checked={info.getValue()}
              onChange={checked =>
                updateCustomTender(checked, info.row.original.id, 'allowsTips')
              }
            />
          );
        },
        size: 110,
      }),
      columnHelper.accessor('autoConfirmTip', {
        header: t(
          'settingsModule.paymentsSettings.customTenders.autoConfirmTip',
        ),
        cell: info => {
          return (
            <Checkbox
              key={`auto-confirm-tip-checkbox-${info.row.original.id}-${info.row.original.autoConfirmTip}`}
              checked={info.getValue()}
              onChange={checked =>
                updateCustomTender(
                  checked,
                  info.row.original.id,
                  'autoConfirmTip',
                )
              }
            />
          );
        },
        size: 110,
      }),
      columnHelper.accessor('printReceipt', {
        header: t('settingsModule.paymentsSettings.customTenders.printReceipt'),
        cell: info => {
          return (
            <Checkbox
              key={`print-receipt-checkbox-${info.row.original.id}-${info.row.original.printReceipt}`}
              checked={info.getValue()}
              onChange={checked =>
                updateCustomTender(
                  checked,
                  info.row.original.id,
                  'printReceipt',
                )
              }
            />
          );
        },
        size: 110,
      }),
      columnHelper.accessor('printMerchantCopy', {
        header: t(
          'settingsModule.paymentsSettings.customTenders.printMerchantCopy',
        ),
        cell: info => {
          return (
            <Checkbox
              key={`print-merchant-copy-checkbox-${info.row.original.id}-${info.row.original.printMerchantCopy}`}
              checked={info.getValue()}
              onChange={checked =>
                updateCustomTender(
                  checked,
                  info.row.original.id,
                  'printMerchantCopy',
                )
              }
            />
          );
        },
        size: 110,
      }),
      columnHelper.accessor('printTipTotalLines', {
        header: t(
          'settingsModule.paymentsSettings.customTenders.printTipTotalLines',
        ),
        cell: info => {
          return (
            <Checkbox
              key={`print-tip-total-lines-checkbox-${info.row.original.id}-${info.row.original.printTipTotalLines}`}
              checked={info.getValue()}
              onChange={checked =>
                updateCustomTender(
                  checked,
                  info.row.original.id,
                  'printTipTotalLines',
                )
              }
            />
          );
        },
        size: 110,
      }),
      columnHelper.accessor('backgroundColor', {
        header: t('app.modals.rolesDiffModal.basic.backgroundColor'),
        cell: info => (
          <ColorPicker
            color={info.getValue() || '#ffffff'}
            onChange={color =>
              updateCustomTender(color, info.row.original.id, 'backgroundColor')
            }
            onReset={() =>
              updateCustomTender(null, info.row.original.id, 'backgroundColor')
            }
          />
        ),
        size: 100,
      }),
      columnHelper.accessor('textColor', {
        header: t('app.modals.rolesDiffModal.basic.textColor'),
        cell: info => (
          <ColorPicker
            color={info.getValue() || '#000000'}
            onChange={color =>
              updateCustomTender(color, info.row.original.id, 'textColor')
            }
            onReset={() =>
              updateCustomTender(null, info.row.original.id, 'textColor')
            }
          />
        ),
        size: 100,
      }),
      columnHelper.accessor('canDelete', {
        header: 'Delete',
        cell: info => (
          <Button
            variant="danger"
            csx={{ width: '50px' }}
            icon={<Icon name="MdDeleteForever" />}
            disabled={!info.getValue() && !isMasterMode}
            onClick={() => handleDelete(info.row.original.id)}
          />
        ),
        size: 70,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMasterMode, t],
  );

  const customTendersArray = useMemo<ICustomTendersTable[]>(() => {
    return orderBy(
      Object.entries(data || {}).map(([id, customTender]) => {
        return {
          id: id,
          status: customTender?.active,
          name: customTender?.name,
          allowsTips: customTender?.allowsTips,
          autoConfirmTip: customTender?.autoConfirmTip,
          printReceipt: customTender?.printReceipt,
          printMerchantCopy: customTender?.printMerchantCopy,
          printTipTotalLines: customTender?.printTipTotalLines,
          backgroundColor:
            customTender?.backgroundColor ||
            theme.colors[DEFAULT_ORDER_SEAT_TAG_COLOR],
          textColor:
            customTender?.textColor ||
            theme.colors[DEFAULT_ORDER_SEAT_TAG_TEXT_COLOR],
          canDelete: actualCustomTenders[id] ? false : true,
          sortPriority: customTender?.sortPriority,
        };
      }),
      'sortPriority',
    );
  }, [data, theme.colors, actualCustomTenders]);

  const handleSort = (newData: ICustomTendersTable[]) => {
    const clonedOrderSeatTags = cloneDeep(data);
    newData.forEach((item, index) => {
      clonedOrderSeatTags[item.id].sortPriority = index + 1;
    });
    setValue<IPaymentsSettings>(
      {
        customTenders: clonedOrderSeatTags,
      },
      SECTION_ID,
    );
  };

  return (
    <div>
      <m.div
        style={{ backgroundColor, boxShadow }}
        css={{
          position: 'sticky',
          top: '80px',
          zIndex: 4,
        }}>
        <HeaderTitle
          title={t('settingsModule.paymentsSettings.customTenders.title')}
          headerCsx={{
            padding: '15px',
          }}
          options={
            <Button
              variant="primary"
              icon={<Icon name="MdAdd" />}
              onClick={onNewCustomTender}>
              {t(
                'settingsModule.paymentsSettings.customTenders.addCustomTender',
              )}
            </Button>
          }
        />
      </m.div>
      <Box
        csx={{
          padding: '15px',
        }}>
        <Table
          mode={ETableModes.SORT_VERTICALLY}
          data={customTendersArray}
          columns={COLUMN_VALUES}
          onSortEnd={handleSort}
          cellCsx={{ height: '70px' }}
          alignHeaders={{
            allowsTips: 'center',
            autoConfirmTip: 'center',
            delete: 'center',
            printReceipt: 'center',
            printMerchantCopy: 'center',
            printTipTotalLines: 'center',
            backgroundColor: 'center',
            textColor: 'center',
          }}
          align={{
            allowsTips: 'center',
            autoConfirmTip: 'center',
            delete: 'center',
            printReceipt: 'center',
            printMerchantCopy: 'center',
            printTipTotalLines: 'center',
            backgroundColor: 'center',
            textColor: 'center',
          }}
          requiredColumns={['name']}
          rowIdPrefix={ROW_ID_PREFIX}
          renderEmptyValues
        />
      </Box>
    </div>
  );
};

export default CustomTenders;
